import { usePostHog } from 'posthog-js/react'
import { useEventDeduplication } from '../hooks/useEventDeduplication'
import { useAuth } from '../context/AuthContext'
import { withProductEmail } from './productEventEmail'

// Business event helpers for Memara-specific tracking
export const useMemoryAnalytics = () => {
  const posthog = usePostHog()
  const { user } = useAuth()
  const captureProduct = (event: string, properties: Record<string, unknown>) => {
    posthog?.capture(event, withProductEmail(properties, user?.email))
  }
  
  return {
    trackMemoryCreated: (data: {
      memory_id: string
      content_length: number
      tags: string[]
      space_id?: string
      source?: string
    }) => {
      captureProduct('memory_created', {
        memory_id: data.memory_id,
        content_length: data.content_length,
        tags: data.tags,
        space_id: data.space_id,
        source: data.source || 'manual',
        timestamp: new Date().toISOString()
      })
    },
    
    trackMemorySearched: (query: string, results_count: number, search_time_ms?: number) => {
      captureProduct('memory_search', {
        search_query: query,
        results_count,
        search_time_ms,
        query_length: query.length,
        has_results: results_count > 0,
        search_timestamp: new Date().toISOString()
      })
    },
    
    trackMemoryViewed: (memory_id: string, view_duration_ms?: number) => {
      captureProduct('memory_viewed', {
        memory_id,
        view_duration_ms,
        timestamp: new Date().toISOString()
      })
    },
    
    trackMemoryUpdated: (memory_id: string, changes: string[]) => {
      captureProduct('memory_updated', {
        memory_id,
        changes_made: changes,
        change_count: changes.length,
        timestamp: new Date().toISOString()
      })
    },
    
    trackMemoryDeleted: (memory_id: string, reason?: string) => {
      captureProduct('memory_deleted', {
        memory_id,
        deletion_reason: reason || 'user_action',
        timestamp: new Date().toISOString()
      })
    }
  }
}

export const useRealmAnalytics = () => {
  const posthog = usePostHog()
  const { user } = useAuth()
  const captureProduct = (event: string, properties: Record<string, unknown>) => {
    posthog?.capture(event, withProductEmail(properties, user?.email))
  }
  
  return {
    trackRealmTransition: (
      from: 'outer' | 'sanctuary',
      to: 'outer' | 'sanctuary',
      duration: number,
      trigger?: string
    ) => {
      captureProduct('realm_transition', {
        from_realm: from,
        to_realm: to,
        transition_duration_ms: duration,
        is_smooth_transition: duration < 700, // Memara's 700ms target
        transition_trigger: trigger || 'user_navigation',
        timestamp: new Date().toISOString()
      })
    },

    trackRealmEngagement: (
      realm: 'outer' | 'sanctuary',
      session_duration_ms: number,
      actions_performed: number
    ) => {
      captureProduct('realm_engagement', {
        realm,
        session_duration_ms,
        actions_performed,
        engagement_rate: actions_performed / (session_duration_ms / 1000), // actions per second
        timestamp: new Date().toISOString()
      })
    }
  }
}

export const useBusinessAnalytics = () => {
  const posthog = usePostHog()
  const { user } = useAuth()
  const { shouldPreventDuplicateEvent } = useEventDeduplication()
  const captureProduct = (event: string, properties: Record<string, unknown>) => {
    posthog?.capture(event, withProductEmail(properties, user?.email))
  }
  
  return {
    trackSubscriptionEvent: (event: 'started' | 'upgraded' | 'cancelled' | 'renewed', data: {
      plan: string
      revenue?: number
      billing_cycle?: 'monthly' | 'yearly'
      previous_plan?: string
    }) => {
      captureProduct(`subscription_${event}`, {
        plan: data.plan,
        revenue: data.revenue,
        billing_cycle: data.billing_cycle,
        previous_plan: data.previous_plan,
        event_timestamp: new Date().toISOString()
      })
    },
    
    trackFeatureUsed: (feature: string, context?: Record<string, any>) => {
      captureProduct('feature_used', {
        feature_name: feature,
        usage_context: context,
        timestamp: new Date().toISOString()
      })
    },

    trackOnboardingStep: (step: string, completed: boolean, time_spent_ms?: number) => {
      // 🔧 FIX: Prevent duplicate onboarding_step events
      const eventKey = {
        eventName: 'onboarding_step',
        userId: user?.id,
        additionalKey: `${step}_${completed}`
      };

      if (!shouldPreventDuplicateEvent(eventKey)) {
        captureProduct('onboarding_step', {
          step_name: step,
          completed,
          time_spent_ms,
          timestamp: new Date().toISOString()
        })
      }
    },

    trackConversionEvent: (event: 'signup' | 'email_verified' | 'first_memory' | 'subscription_intent') => {
      captureProduct('conversion_event', {
        event_type: event,
        timestamp: new Date().toISOString()
      })
    },

    trackUserFeedback: (rating: number, feedback: string, context?: string) => {
      captureProduct('user_feedback', {
        rating,
        feedback,
        feedback_length: feedback.length,
        context: context || 'general',
        timestamp: new Date().toISOString()
      })
    },

    trackUpgradeClick: (data: {
      source: 'upgrade_prompt' | 'billing_page' | 'tier_status' | 'usage_display' | 'feature_gate' | 'navbar' | 'dashboard' | 'pricing_modal' | 'analytics_page' | 'tier_info_display' | 'inline_prompt'
      current_tier: string
      target_tier: string
      upgrade_context?: 'memory_limit' | 'space_limit' | 'api_rate_limit' | 'feature_access' | 'memory_expiration' | 'general'
      element_type?: 'button' | 'modal' | 'card' | 'banner' | 'prompt'
      trigger_reason?: string
      additional_context?: Record<string, any>
    }) => {
      captureProduct('upgrade_click', {
        source: data.source,
        current_tier: data.current_tier,
        target_tier: data.target_tier,
        upgrade_context: data.upgrade_context || 'general',
        element_type: data.element_type || 'button',
        trigger_reason: data.trigger_reason,
        tier_progression: `${data.current_tier}_to_${data.target_tier}`,
        is_tier_upgrade: data.current_tier !== data.target_tier,
        ...data.additional_context,
        timestamp: new Date().toISOString()
      })
    },

    trackUpgradeModalView: (data: {
      source: string
      current_tier: string
      upgrade_context: string
      trigger_reason?: string
    }) => {
      captureProduct('upgrade_modal_view', {
        source: data.source,
        current_tier: data.current_tier,
        upgrade_context: data.upgrade_context,
        trigger_reason: data.trigger_reason,
        timestamp: new Date().toISOString()
      })
    },

    trackPlanSelection: (data: {
      source: string
      selected_plan: string
      billing_cycle: 'monthly' | 'annual'
      current_tier: string
      coupon_code?: string
    }) => {
      captureProduct('plan_selection', {
        source: data.source,
        selected_plan: data.selected_plan,
        billing_cycle: data.billing_cycle,
        current_tier: data.current_tier,
        coupon_code: data.coupon_code,
        tier_progression: `${data.current_tier}_to_${data.selected_plan}`,
        is_downgrade: data.selected_plan === 'free' && data.current_tier !== 'free',
        timestamp: new Date().toISOString()
      })
    }
  }
}

export const useErrorAnalytics = () => {
  const posthog = usePostHog()
  
  return {
    trackError: (error: Error, context?: Record<string, any>) => {
      posthog?.captureException(error, {
        error_context: context,
        timestamp: new Date().toISOString()
      })
    },

    trackApiError: (endpoint: string, status: number, error_message: string) => {
      posthog?.capture('api_error', {
        endpoint,
        status_code: status,
        error_message,
        timestamp: new Date().toISOString()
      })
    }
  }
}

export const usePerformanceAnalytics = () => {
  const posthog = usePostHog()
  
  return {
    trackPageLoad: (page: string, load_time_ms: number) => {
      posthog?.capture('page_load', {
        page_name: page,
        load_time_ms,
        is_fast_load: load_time_ms < 1000,
        timestamp: new Date().toISOString()
      })
    },

    trackSearchPerformance: (query: string, results_count: number, search_time_ms: number) => {
      posthog?.capture('search_performance', {
        query,
        results_count,
        search_time_ms,
        is_fast_search: search_time_ms < 500,
        timestamp: new Date().toISOString()
      })
    }
  }
} 